import { useCallback, useEffect, useRef, useState } from 'react';
import { getCapture } from '@/lib/api';
import type { CaptureDetailResponse, CaptureStatus } from '@/lib/api/types';
import { shouldPoll } from './review-model';

export const POLL_INTERVAL_MS = 2500;
/** About two minutes, past the extraction consumer's own 240s timeout being
 *  hit twice; a capture still working after this is stuck, not slow. */
export const MAX_POLLS = 48;

export interface CaptureRecordState {
  record: CaptureDetailResponse | null;
  error: string | null;
  /** The poll ran out while the capture was still working. */
  stalled: boolean;
  /** Reads the record once, without restarting the poll. */
  reload: () => Promise<CaptureDetailResponse | null>;
  /**
   * Marks the capture with the status a mutation answered (EXTRACTING after
   * a retake or a rerun) and restarts the poll, so the screen shows the
   * Reading state at once rather than after the next read.
   */
  watch: (status?: CaptureStatus) => void;
}

/**
 * The capture behind the review route, read again while the pipeline works
 * on it or a voice note is out for transcription, and left alone the
 * moment neither holds, so a finished capture costs one request.
 */
export function useCaptureRecord(captureId: string | undefined): CaptureRecordState {
  const [record, setRecord] = useState<CaptureDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stalled, setStalled] = useState(false);
  const [generation, setGeneration] = useState(0);
  const pollsRef = useRef(0);

  const reload = useCallback(async (): Promise<CaptureDetailResponse | null> => {
    if (!captureId) return null;
    try {
      const result = await getCapture(captureId);
      setRecord(result);
      setError(null);
      return result;
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Failed to load capture');
      return null;
    }
  }, [captureId]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    pollsRef.current = 0;
    setStalled(false);

    async function tick(): Promise<void> {
      const result = await reload();
      if (cancelled || !result) return;
      if (!shouldPoll(result)) return;
      pollsRef.current += 1;
      if (pollsRef.current >= MAX_POLLS) {
        setStalled(true);
        return;
      }
      timer = setTimeout(() => void tick(), POLL_INTERVAL_MS);
    }

    // A watch() right after a mutation waits one interval before reading,
    // so the 202's EXTRACTING is on screen before the first read answers.
    if (generation === 0) {
      void tick();
    } else {
      timer = setTimeout(() => void tick(), POLL_INTERVAL_MS);
    }
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [reload, generation]);

  const watch = useCallback((status?: CaptureStatus) => {
    if (status) setRecord((current) => (current ? { ...current, status } : current));
    setGeneration((value) => value + 1);
  }, []);

  return { record, error, stalled, reload, watch };
}
